import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  TextField,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import { ChevronDown, ChevronRight } from "lucide-react";
import AdminSideNav from "../../../adminComponents/sidenav";
import AdminHeader from "../../../adminComponents/header";
import axiosInstance from "../../../services/axiosInstance";

const COMMON = "__common__";
const COMMON_OPTION = { _id: COMMON, label: "Common configuration (all clients)" };
const clientLabel = (o) => (o._id === COMMON ? o.label : [o.companyName || o.name, o.email].filter(Boolean).join(" · "));

// Parents with children, in registry order.
const buildTree = (menus) => {
  const roots = menus.filter((m) => !m.parentKey).map((m) => ({ ...m, children: [] }));
  const byKey = Object.fromEntries(roots.map((r) => [r.key, r]));
  menus.filter((m) => m.parentKey).forEach((m) => byKey[m.parentKey]?.children.push(m));
  return roots;
};

const SidebarSettings = () => {
  const isSuper = localStorage.getItem("role") === "superadmin";
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null); // option object for the chosen client
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState(COMMON);
  const [menus, setMenus] = useState([]);
  const [disabled, setDisabled] = useState([]);
  const [commonDisabled, setCommonDisabled] = useState([]);
  const [inherit, setInherit] = useState(true);
  const [saved, setSaved] = useState({ disabled: [], inherit: true });
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  const isCommon = target === COMMON;
  const readOnly = !isCommon && inherit; // inheriting: edit only after unchecking

  useEffect(() => {
    if (!isSuper) setTarget("");
  }, [isSuper]);

  // Server-side search by company name / name / email, debounced.
  useEffect(() => {
    const t = setTimeout(() => {
      axiosInstance
        .get("/sidebar/clients", { params: { search: query, limit: 20 } })
        .then(({ data }) => setClients(data.clients || []))
        .catch((e) => setError(e.response?.data?.message || "Failed to load clients"));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(async () => {
    if (!target) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (target === COMMON) {
        const { data } = await axiosInstance.get("/sidebar/configuration/common");
        setMenus(data.menus);
        setDisabled(data.disabledKeys);
        setSaved({ disabled: data.disabledKeys, inherit: true });
        setCommonDisabled(data.disabledKeys);
        setInherit(true);
      } else {
        const [{ data }, common] = await Promise.all([
          axiosInstance.get(`/sidebar/clients/${target}/configuration`),
          axiosInstance.get("/sidebar/configuration/common"),
        ]);
        setMenus(data.menus);
        setCommonDisabled(common.data.disabledKeys);
        // Start the editable set from the common config so un-inheriting is a tweak, not a blank slate.
        const start = data.inheritCommon ? common.data.disabledKeys : data.disabledKeys;
        setDisabled(start);
        setInherit(data.inheritCommon);
        setSaved({ disabled: start, inherit: data.inheritCommon });
      }
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load configuration");
    } finally {
      setLoading(false);
    }
  }, [target]);

  useEffect(() => {
    load();
  }, [load]);

  const tree = useMemo(() => buildTree(menus), [menus]);
  const effective = useMemo(() => {
    const set = new Set(!isCommon && inherit ? commonDisabled : disabled);
    menus.filter((m) => m.parentKey && set.has(m.parentKey)).forEach((m) => set.add(m.key));
    return set;
  }, [menus, disabled, inherit, isCommon, commonDisabled]);

  const dirty =
    inherit !== saved.inherit || JSON.stringify([...disabled].sort()) !== JSON.stringify([...saved.disabled].sort());

  const toggle = (key) =>
    setDisabled((d) => (d.includes(key) ? d.filter((k) => k !== key) : [...d, key]));

  const save = async () => {
    setBusy(true);
    try {
      if (isCommon) {
        await axiosInstance.put("/sidebar/configuration/common", { disabledKeys: disabled });
      } else {
        await axiosInstance.put(`/sidebar/clients/${target}/configuration`, { disabledKeys: disabled, inheritCommon: inherit });
      }
      setToast({ severity: "success", text: "Sidebar configuration saved" });
      await load();
    } catch (e) {
      setToast({ severity: "error", text: e.response?.data?.message || "Save failed" });
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset this client to the common configuration?")) return;
    setBusy(true);
    try {
      await axiosInstance.post(`/sidebar/clients/${target}/configuration/reset`);
      setToast({ severity: "success", text: "Client now inherits the common configuration" });
      await load();
    } catch (e) {
      setToast({ severity: "error", text: e.response?.data?.message || "Reset failed" });
    } finally {
      setBusy(false);
    }
  };

  const renderRow = (m, child) => (
    <Box key={m.key} sx={{ pl: child ? 5 : 0, display: "flex", alignItems: "center" }}>
      <Checkbox
        size="small"
        checked={!effective.has(m.key)}
        disabled={readOnly || (child && effective.has(m.parentKey))}
        onChange={() => toggle(m.key)}
      />
      <Typography variant="body2" sx={{ color: effective.has(m.key) ? "text.disabled" : "text.primary" }}>
        {m.label}
      </Typography>
      {m.path && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {m.path}
        </Typography>
      )}
    </Box>
  );

  const clientName = selected;

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ ml: { xs: "0px", md: "200px" } }}>
        <AdminSideNav />
        <Box sx={{ display: { xs: "none", md: "initial" } }}>
          <AdminHeader title="Sidebar Settings" />
        </Box>
        <Box component="main" sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
            <Autocomplete
              size="small"
              sx={{ width: 360 }}
              options={[...(isSuper ? [COMMON_OPTION] : []), ...clients]}
              value={target === COMMON ? COMMON_OPTION : selected}
              getOptionLabel={clientLabel}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              filterOptions={(o) => o}
              onInputChange={(_, v, reason) => reason === "input" && setQuery(v)}
              onChange={(_, v) => {
                setSelected(v && v._id !== COMMON ? v : null);
                setTarget(v ? v._id : isSuper ? COMMON : "");
                setQuery("");
              }}
              noOptionsText="No clients found"
              renderOption={(props, o) => (
                <li {...props} key={o._id}>
                  <Box>
                    <Typography variant="body2">{o._id === COMMON ? o.label : o.companyName || o.name || o.email}</Typography>
                    {o._id !== COMMON && (
                      <Typography variant="caption" color="text.secondary">
                        {[o.name, o.email].filter(Boolean).join(" · ")}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Client (search by name or email)" />}
            />
            {!isCommon && target && (
              <Chip
                size="small"
                color={inherit ? "default" : "primary"}
                label={inherit ? "Inheriting common configuration" : "Custom client configuration"}
              />
            )}
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!target ? (
            <Alert severity="info">Select a client to configure its sidebar.</Alert>
          ) : loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
          ) : menus.length === 0 ? (
            <Alert severity="info">No menus registered.</Alert>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" }, gap: 3 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {isCommon ? "Common configuration" : clientName?.companyName || clientName?.name || "Client"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isCommon
                    ? "Applies to every client that has no custom configuration."
                    : "A custom configuration replaces the common one for this client only."}
                </Typography>
                {!isCommon && (
                  <FormControlLabel
                    sx={{ display: "block", mt: 1 }}
                    control={<Switch checked={inherit} onChange={(e) => setInherit(e.target.checked)} />}
                    label="Inherit common configuration"
                  />
                )}
                <Divider sx={{ my: 1 }} />
                {tree.map((p) => (
                  <Box key={p.key}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {p.children.length ? (
                        <IconButton size="small" onClick={() => setOpen((o) => ({ ...o, [p.key]: !o[p.key] }))}>
                          {open[p.key] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </IconButton>
                      ) : (
                        <Box sx={{ width: 30 }} />
                      )}
                      {renderRow(p, false)}
                    </Box>
                    <Collapse in={!!open[p.key]} unmountOnExit>
                      {p.children.map((c) => renderRow(c, true))}
                    </Collapse>
                  </Box>
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                  User Profile and Logout are always available. Disabling a menu also blocks its APIs.
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <Button variant="contained" disabled={!dirty || busy} onClick={save}>Save</Button>
                  <Button disabled={!dirty || busy} onClick={() => { setDisabled(saved.disabled); setInherit(saved.inherit); }}>
                    Cancel
                  </Button>
                  {!isCommon && (
                    <Button color="warning" disabled={busy} onClick={reset}>Reset to common</Button>
                  )}
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, alignSelf: "start" }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Preview (as saved + unsaved changes)</Typography>
                {tree.map((p) => {
                  const kids = p.children.filter((c) => !effective.has(c.key));
                  if (effective.has(p.key) || (p.children.length && !kids.length)) return null;
                  return (
                    <Box key={p.key} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={500}>{p.label}</Typography>
                      {kids.map((c) => (
                        <Typography key={c.key} variant="caption" sx={{ display: "block", pl: 2 }} color="text.secondary">
                          {c.label}
                        </Typography>
                      ))}
                    </Box>
                  );
                })}
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">User Profile</Typography>
                <Typography variant="body2" color="text.secondary">Logout</Typography>
              </Paper>
            </Box>
          )}
        </Box>
      </Box>
      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.text}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default SidebarSettings;
